/**
 * Scanner safety patches.
 *
 * @zxing/browser calls a handful of advanced camera APIs (torch,
 * applyConstraints with photo options, ImageCapture.setPhotoOptions)
 * that are OPTIONAL. Several Android Chrome / WebView builds reject
 * these calls asynchronously with the string "setPhotoOptions failed".
 * The failure is non-blocking — barcode decoding keeps working — but
 * the promise rejection surfaces as a red React runtime overlay in
 * dev builds and pollutes production consoles.
 *
 * This module must be imported at the very top of `index.js`, BEFORE
 * React and any other bundle, so our capture-phase listeners run
 * before `react-error-overlay` registers its own.
 *
 * Rules:
 *   - Advanced camera options are optional.
 *   - Barcode scanning is required.
 *   - If advanced options fail, continue silently.
 */

const IGNORED = [
    "setPhotoOptions",
    "setPhotoOptions failed",
    "OverconstrainedError",
    "advanced",
    // ResizeObserver spec-compliant "undelivered notifications" warning is
    // fired by every Chromium browser when observers trigger layout changes
    // on the next frame. It is 100% benign and every major framework filters
    // it explicitly. Without this line it opens the CRA dev-only red overlay
    // every time we open a dialog / animate a component.
    "ResizeObserver loop",
    "ResizeObserver loop completed with undelivered notifications",
    "ResizeObserver loop limit exceeded",
];

function isIgnorable(err) {
    if (!err) return false;
    const msg = String(err.message || err.reason || err.name || err);
    return IGNORED.some((k) => msg.includes(k));
}

let installed = false;

export function installScannerSafety() {
    if (installed || typeof window === "undefined") return;
    installed = true;

    // ---------- 1. Capture-phase global listeners ------------------------
    // Registered with capture=true so they fire before react-error-overlay
    // and any user-space handlers. preventDefault + stopImmediatePropagation
    // combined with a truthy return keep the CRA overlay from opening.
    const onError = (event) => {
        const err = event.error || event.reason || event;
        if (isIgnorable(err) || isIgnorable(event.message)) {
            event.preventDefault?.();
            event.stopImmediatePropagation?.();
            console.warn("[scanner] non-blocking camera error swallowed:", event.message || err);
            return true;
        }
        return undefined;
    };
    const onRejection = (event) => {
        const reason = event.reason;
        if (isIgnorable(reason)) {
            event.preventDefault?.();
            event.stopImmediatePropagation?.();
            console.warn("[scanner] non-blocking promise rejection swallowed:", reason?.message || reason);
            return true;
        }
        return undefined;
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection, true);

    // ---------- 2. Monkey-patch MediaStreamTrack.applyConstraints --------
    // ZXing calls this to enable torch. On unsupported devices it rejects
    // with "setPhotoOptions failed". We downgrade the rejection to a
    // resolved promise so the caller's await returns cleanly.
    try {
        const proto = window.MediaStreamTrack && window.MediaStreamTrack.prototype;
        if (proto && typeof proto.applyConstraints === "function" && !proto.__miseSafeAC) {
            const orig = proto.applyConstraints;
            proto.applyConstraints = function (...args) {
                try {
                    const p = orig.apply(this, args);
                    return Promise.resolve(p).catch((e) => {
                        if (isIgnorable(e)) {
                            console.warn("[scanner] applyConstraints ignored:", e?.message || e);
                            return undefined;
                        }
                        throw e;
                    });
                } catch (e) {
                    if (isIgnorable(e)) {
                        console.warn("[scanner] applyConstraints sync ignored:", e?.message || e);
                        return Promise.resolve();
                    }
                    return Promise.reject(e);
                }
            };
            proto.__miseSafeAC = true;
        }
    } catch (e) {
        console.warn("[scanner] applyConstraints patch failed:", e);
    }

    // ---------- 3. Monkey-patch ImageCapture.setPhotoOptions -------------
    try {
        if (typeof window.ImageCapture === "function") {
            const proto = window.ImageCapture.prototype;
            if (typeof proto.setPhotoOptions === "function" && !proto.__miseSafeSPO) {
                const orig = proto.setPhotoOptions;
                proto.setPhotoOptions = function (...args) {
                    try {
                        const p = orig.apply(this, args);
                        return Promise.resolve(p).catch((e) => {
                            console.warn("[scanner] setPhotoOptions ignored:", e?.message || e);
                            return undefined;
                        });
                    } catch (e) {
                        console.warn("[scanner] setPhotoOptions sync ignored:", e?.message || e);
                        return Promise.resolve();
                    }
                };
                proto.__miseSafeSPO = true;
            }
            // takePhoto / grabFrame can throw the same error family — guard both.
            for (const m of ["takePhoto", "grabFrame"]) {
                if (typeof proto[m] === "function" && !proto[`__miseSafe_${m}`]) {
                    const orig = proto[m];
                    proto[m] = function (...args) {
                        try {
                            const p = orig.apply(this, args);
                            return Promise.resolve(p).catch((e) => {
                                if (isIgnorable(e)) {
                                    console.warn(`[scanner] ${m} ignored:`, e?.message || e);
                                    return undefined;
                                }
                                throw e;
                            });
                        } catch (e) {
                            if (isIgnorable(e)) return Promise.resolve();
                            return Promise.reject(e);
                        }
                    };
                    proto[`__miseSafe_${m}`] = true;
                }
            }
        }
    } catch (e) {
        console.warn("[scanner] ImageCapture patch failed:", e);
    }

    // ---------- 4. Fallback: remove CRA runtime overlay iframe when it
    // displays one of our ignored errors. Runs on every animation frame
    // (idempotent, cheap). This is required because on some browsers our
    // capture-phase listeners can't stop react-error-overlay from opening
    // the overlay iframe. The check is scoped by inspecting the iframe
    // body text so we NEVER touch overlays for real errors.
    const removeIgnoredOverlays = () => {
        try {
            const iframes = document.querySelectorAll("body > iframe");
            for (const f of iframes) {
                let body = "";
                try { body = f.contentDocument?.body?.innerText || ""; } catch (_e) { body = ""; }
                if (body && body.includes("runtime error") && isIgnorable(body)) {
                    try { f.remove(); } catch (_e) { /* */ }
                }
            }
        } catch (_e) { /* */ }
    };
    const tick = () => {
        removeIgnoredOverlays();
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    // Also try the official API when available. Failing this branch is fine
    // — the DOM fallback above already handles the overlay.
    try {
        const overlay = require("react-error-overlay");
        if (overlay && typeof overlay.reportRuntimeError === "function"
                && !overlay.__miseSafeRRE) {
            const origReport = overlay.reportRuntimeError;
            overlay.reportRuntimeError = function (err) {
                if (isIgnorable(err)) {
                    console.warn("[scanner] overlay report suppressed:", err?.message || err);
                    return;
                }
                return origReport.apply(this, arguments);
            };
            overlay.__miseSafeRRE = true;
        }
    } catch (_e) { /* react-error-overlay not resolvable in prod bundle */ }
}

// Auto-install on first import. Idempotent — the flag inside guards.
installScannerSafety();
